import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

let followed = null;
let loading = null;
const subs = new Set();
const emit = () => subs.forEach((f) => f(new Set(followed || [])));
const load = () => (loading ||= api.get("/me/fact-follows").then((r) => { followed = new Set(r.data.map((x) => x.fact_id)); emit(); }).catch(() => { loading = null; }));

export const resetFollowedFacts = () => { followed = null; loading = null; emit(); };

export const useFollowedFacts = () => {
  const [ids, setIds] = useState(followed ? new Set(followed) : new Set());
  useEffect(() => { subs.add(setIds); load(); return () => subs.delete(setIds); }, []);
  return ids;
};

export const toggleFollowFact = async (id, on) => {
  await api[on ? "post" : "delete"](`/facts/${id}/follow`);
  followed = followed || new Set();
  on ? followed.add(id) : followed.delete(id);
  emit();
};

export const FollowedFacts = () => {
  const ids = useFollowedFacts();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/me/fact-follows").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  const shown = (rows || []).filter((r) => ids.has(r.fact_id));
  return (
    <section className="surface space-y-3 p-5 sm:p-6" data-testid="pref-followed-facts">
      <h2 className="text-xl">Facts you follow</h2>
      <p className="text-xs text-slate-500">An in-app alert only if one becomes disputed by approved sources or is withdrawn. Follow any fact from its card or page.</p>
      {rows && !shown.length && <p className="text-sm text-slate-500" data-testid="pref-followed-facts-empty">You aren't following any facts yet.</p>}
      {shown.map((r) => (
        <div key={r.fact_id} className="flex items-center gap-3" data-testid={`pref-followed-fact-${r.fact_id}`}>
          <Link to={`/facts/${r.fact_id}`} className="min-w-0 flex-1 truncate text-sm hover:underline">{r.headline}</Link>
          <span className="shrink-0 text-[11px] font-semibold uppercase text-slate-500">{r.withdrawn ? "Withdrawn" : r.status}</span>
          <FollowFact id={r.fact_id} compact />
        </div>
      ))}
    </section>
  );
};

export const FollowFact = ({ id, compact }) => {
  const ids = useFollowedFacts();
  const on = ids.has(id);
  const click = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await toggleFollowFact(id, !on);
      toast.success(on ? "Unfollowed. You won't get alerts for this fact" : "Following. We'll alert you if this fact becomes disputed or is withdrawn");
    } catch { toast.error("Couldn't update. Try again"); }
  };
  const Icon = on ? BellRing : Bell;
  return (
    <button type="button" onClick={click} aria-pressed={on} data-testid={`fact-follow-${id}`}
      className={`inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors ${compact ? "px-2.5 py-1 text-xs" : "px-4 py-2 text-sm"} ${on ? "border-[hsl(var(--facts))] bg-[hsl(var(--facts))] text-white hover:opacity-90" : "border-border bg-white text-slate-700 hover:border-slate-400"}`}>
      <Icon size={compact ? 12 : 14} />{on ? "Following" : "Follow"}
    </button>
  );
};
